import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { track } from "@/lib/analytics";

export const PLANS = [
  {
    name: "Property Care",
    forWhom: "For owners who need occasional assistance.",
    includes: ["Property inspections", "Maintenance coordination", "Cleaning", "Repairs", "Vendor coordination", "Emergency assistance"],
    pricing: "Case-to-case",
    featured: false,
  },
  {
    name: "Rental Management",
    forWhom: "For owners who have tenants.",
    includes: [
      "Tenant coordination", "Rent tracking", "Rent collection assistance", "Owner reporting",
      "Lease renewal coordination", "Tenant verification assistance", "Move-in / move-out coordination", "Maintenance coordination",
    ],
    pricing: "Case-to-case / monthly management fee",
    featured: true,
  },
  {
    name: "Complete Property Management",
    forWhom: "For owners who want almost everything handled.",
    includes: [
      "Tenant sourcing & verification", "Rental agreement coordination", "Rent collection & owner reporting",
      "Property inspections", "Maintenance, repairs & cleaning", "Move-in / move-out & lease renewal",
      "Vendor management", "Property make-ready & re-rental", "Emergency coordination",
    ],
    pricing: "Customized to property, rent value & scope",
    featured: false,
  },
];

export const PricingTeaser = () => (
  <section className="bg-ivory py-24 sm:py-32" data-testid="pricing-teaser-section">
    <div className="container-site">
      <SectionHeading
        chapter="10"
        eyebrow="Transparent Pricing"
        align="center"
        title="Flexible Plans. No Rigid Packages."
        copy="Every property is different — so is every quote. You pay for the scope you actually need."
      />

      <Stagger className="mt-14 grid gap-5 lg:grid-cols-3" gap={0.09}>
        {PLANS.map((p) => (
          <StaggerItem key={p.name}>
            <div
              className={`relative flex h-full flex-col rounded-3xl p-8 sm:p-9 ${
                p.featured
                  ? "bg-navy-900 text-white shadow-[0_35px_70px_-25px_rgba(10,17,40,0.55)]"
                  : "border border-navy-900/10 bg-white"
              }`}
              data-testid={`plan-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {p.featured && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-gold-500 px-4 py-1.5 font-display text-[10px] font-extrabold uppercase tracking-[0.2em] text-navy-900">
                  Most Chosen
                </span>
              )}
              <h3 className={`font-display text-xl font-extrabold tracking-tight sm:text-2xl ${p.featured ? "text-white" : "text-navy-900"}`}>
                {p.name}
              </h3>
              <p className={`mt-2 text-sm ${p.featured ? "text-white/60" : "text-muted-foreground"}`}>{p.forWhom}</p>
              <ul className="mt-7 flex-1 space-y-3">
                {p.includes.map((item) => (
                  <li key={item} className={`flex items-start gap-2.5 text-sm ${p.featured ? "text-white/85" : "text-navy-900/80"}`}>
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${p.featured ? "text-gold-400" : "text-gold-600"}`} />
                    {item}
                  </li>
                ))}
              </ul>
              <p className={`mt-8 font-display text-xs font-bold uppercase tracking-[0.2em] ${p.featured ? "text-gold-400" : "text-gold-600"}`}>
                {p.pricing}
              </p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>

      <Reveal delay={0.15}>
        <div className="mt-12 flex flex-col items-center gap-6 text-center">
          <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
            We aim to offer highly competitive pricing — typically targeting 5–10% better value than
            comparable services for equivalent scope. No hidden maintenance markup without prior disclosure.
          </p>
          <Link
            to="/pricing"
            className="btn-navy"
            data-testid="pricing-teaser-cta"
            onClick={() => track("quote_request", { location: "pricing_teaser" })}
          >
            Request My Property Management Quote <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
